import { ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Event } from 'src/app/model/event';
import { SelectOption } from 'src/app/model/select.option';
import { CheckListAnswer, CheckListField } from 'src/app/model/checklist-field';
import { AdminService } from 'src/app/service/admin.service';
import { PublicationEventInfo, PublicationService, SubmissionCoverage } from 'src/app/service/publication.service';
import { FormFieldType } from 'src/app/enum/form.field.type';
import { FormFieldCategory } from 'src/app/enum/form-field-category';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { Observable, forkJoin, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NavbarService } from 'src/app/service/navbar.service';
import { NotificationService } from 'src/app/service/notification.service';
import { EVENT_ADMIN_ICONS } from '../../event-admin-icons';
import { HasUnsavedChanges } from 'src/app/guard/unsaved-changes.guard';
import { PublicationFixedControlComponent } from 'src/app/component/publication-fixed-control/publication-fixed-control.component';

@Component({
  selector: 'app-event-publication',
  templateUrl: './event-publication.component.html',
  styleUrls: ['./event-publication.component.scss']
})
export class EventPublicationComponent implements OnInit, HasUnsavedChanges {

  // As duas tabelas de submissões da página (Registro de autor e Transferência).
  // Só elas acumulam alterações não salvas: os toggles e os campos IEEE têm
  // auto-save. Consultadas pelo `UnsavedChangesGuard` da rota.
  @ViewChildren(PublicationFixedControlComponent) fixedControls: QueryList<PublicationFixedControlComponent>;

  hasUnsavedChanges(): boolean {
    return this.controlsDirty || (this.fixedControls?.some(control => control.dirty) ?? false);
  }

  // Mapa global de ícones (sidebar + page headers — single source of truth).
  icons = EVENT_ADMIN_ICONS;

  // Aba ativa no `<app-chair-tabs>` (sistema de abas do JEMS3).
  selectedTabIndex = 0;

  // Índice de cada aba de monitoramento. Elas só existem quando o toggle
  // correspondente está ligado (`*ngIf`), então a numeração desliza. Serve pra
  // dizer ao `<app-publication-fixed-control>` se ele está na aba VISÍVEL — as
  // inativas continuam montadas (carrossel) e a barra de confirmação de uma
  // aba escondida não pode decolar por cima da que está à vista.
  get registrationTabIndex(): number {
    return 1;
  }

  get copyrightTabIndex(): number {
    // A aba de Registro pode estar VISÍVEL por estado pendente (ainda não
    // salvo), e nesse caso ela também ocupa uma posição — o índice tem que
    // seguir a visibilidade, não o que está no servidor.
    return this.tabVisible('authorRegistrationEnable') ? 2 : 1;
  }

  // Inputs de título dos controles personalizados — usados pra focar a linha
  // recém-adicionada.
  @ViewChildren('customControlInput') private customControlInputs: QueryList<ElementRef<HTMLInputElement>>;

  // Id do evento resolvido da ROTA (com fallback pro AdminService). Antes o
  // componente lia `adminService.eventId` de dentro de um setTimeout, torcendo
  // pra que o componente-pai já o tivesse preenchido; ler a rota é determinístico.
  private eventId: number;

  authorRegistrationForm: FormGroup;
  copyrightForm: FormGroup;
  // Tabela ÚNICA: 3 linhas fixas + N personalizadas, todas CheckListField.
  controlsForm: FormArray;
  authorRegistrationAnswers: FormArray;
  copyrightAnswers: FormArray;
  event: Event | PublicationEventInfo;

  checklistFields: Array<CheckListField> = [];

  checklistAnswers = {
    'AUTHOR_REGISTRATION': [],
    'COPYRIGHT': []
  }

  FormFieldCategory = FormFieldCategory;

  enabledIEEECF = false;

  // Opções do formulário eletrônico de transferência de copyright. Os rótulos
  // são traduzidos aqui (e não no template) porque `app-select-input` recebe as
  // opções já prontas. 'NONE' é o default do backend.
  readonly copyrightFormOptions: SelectOption[] = [
    new SelectOption('NONE', this.translate.instant('admin.event.publication.controls.form.copyright-form-none')),
    new SelectOption('IEEE', this.translate.instant('admin.event.publication.controls.form.copyright-form-ieee')),
    new SelectOption('SBC', this.translate.instant('admin.event.publication.controls.form.copyright-form-sbc'))
  ];

  constructor(
    private adminService: AdminService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private navbarService: NavbarService,
    private publicationService: PublicationService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const routeEventId = Number.parseInt(this.route.snapshot.parent?.params?.eventId, 10);
    this.eventId = Number.isNaN(routeEventId) ? this.adminService.eventId : routeEventId;

    this.publicationService.getPublicationEventInfo(this.eventId).subscribe(publicationEventInfo => {
      this.event = publicationEventInfo;
      this.initComponent();
    }, () => {
      // Antes o erro era engolido (`() => {}`) e a tela ficava em branco sem explicação.
      this.notificationService.notifyError('admin.event.publication.controls.load-failed');
    });
  }

  private initComponent(): void {

    // As respostas de cada categoria só são buscadas quando o monitoramento
    // correspondente está LIGADO — desligado, a aba nem existe (`*ngIf` no
    // template) e a lista seria baixada para nada. É a requisição mais cara da
    // tela; num evento que usa só um dos dois controles, isso corta metade do
    // custo de carga. Os forms são inicializados de qualquer forma, porque o
    // `*ngIf` do corpo da página depende deles.
    if (this.event.authorRegistrationEnable) {
      this.publicationService.getSubmissionsAnswers(this.eventId, {category: FormFieldCategory.AUTHOR}).subscribe(answers => {
        this.checklistAnswers.AUTHOR_REGISTRATION = answers;
        this.initAuthorRegistrationForm();
      }, () => {
        this.notificationService.notifyError('admin.event.publication.controls.load-failed');
      });
    } else {
      this.initAuthorRegistrationForm();
    }

    if (this.event.copyrightEnable) {
      this.publicationService.getSubmissionsAnswers(this.eventId, {category: FormFieldCategory.COPYRIGHT}).subscribe(answers => {
        this.checklistAnswers.COPYRIGHT = answers;
        this.initCopyrightForm();
      }, () => {
        this.notificationService.notifyError('admin.event.publication.controls.load-failed');
      });
    } else {
      this.initCopyrightForm();
    }

    // Chamada ÚNICA: antes `getEventCheckListFields` era disparado duas vezes
    // (uma delas só sobrescrevia `checklistFields` sem montar o form).
    this.publicationService.getEventCheckListFields(this.eventId, true).subscribe(checklistFields => {
      this.checklistFields = checklistFields;
      this.initControlsForm();
    }, () => {
      this.notificationService.notifyError('admin.event.publication.controls.load-failed');
    });
  }

  private initAuthorRegistrationForm() : void {
    this.authorRegistrationForm = this.fb.group({
      submissions: [this.listToText(this.checklistAnswers.AUTHOR_REGISTRATION.map(a => a.submissionId))]
    });

    this.initAuthorRegistrationAnswersForm();
  }

  private initAuthorRegistrationAnswersForm() : void {
    this.checklistAnswers.AUTHOR_REGISTRATION.push(new CheckListAnswer());
    this.authorRegistrationAnswers = this.fb.array(this.checklistAnswers.AUTHOR_REGISTRATION.map((answer, index) => this.fb.group({
      id: [answer.id],
      order: [index + 1],
      status: [answer.status],
      submissionId: [answer.submissionId]
    })));
  }

  private initCopyrightAnswersForm() : void {
    this.checklistAnswers.COPYRIGHT.push(new CheckListAnswer());
    this.copyrightAnswers = this.fb.array(this.checklistAnswers.COPYRIGHT.map((answer, index) => this.fb.group({
      id: [answer.id],
      order: [index + 1],
      status: [answer.status],
      submissionId: [answer.submissionId]
    })));
  }

  private initCopyrightForm() : void {
    this.copyrightForm = this.fb.group({
      // Enum NONE/IEEE/SBC. O evento pode vir de um backend que ainda não
      // conhece o campo (ou de um registro anterior à migração): nesse caso o
      // booleano antigo diz o que era possível dizer.
      copyrightForm: [this.event.copyrightForm
        || (this.event.ieeeCopyrightEnable ? 'IEEE' : 'NONE')],
      pubTitle: [this.event.ieeePubTitle],
      artSource: [this.event.ieeeArtSource],
      partNumber: [this.event.ieeePartNumber],
      submissions: [this.listToText(this.checklistAnswers.COPYRIGHT.map(a => a.submissionId))]
    });

    this.initCopyrightAnswersForm();

    // Persistência dos campos IEEE. Antes eles SÓ eram gravados de carona no
    // submit da lista de submissões (`submitFixedControlForm`): quem editasse
    // apenas os três campos e saísse da tela perdia o que digitou.
    // Auto-save no mesmo espírito dos toggles da aba Geral, com debounce pra não
    // disparar um POST por tecla. NÃO condicionamos ao form estar válido: com o
    // eCF ligado, título e fonte são obrigatórios, e exigir validade faria o
    // próprio ato de LIGAR o eCF não persistir (os campos ainda estão vazios
    // nesse instante). O `required` continua sinalizando o que falta preencher.
    // O `distinctUntilChanged` ignora mudanças no campo `submissions`, que faz
    // parte do form mas não é gravado por aqui.
    this.copyrightForm.valueChanges.pipe(
      map(() => this.ieeeControlValue()),
      debounceTime(600),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ).subscribe(() => this.submitIeeeCopyrightControl());

    // Os três campos do eCF pertencem ao IEEE: só existem nessa opção.
    this.enabledIEEECF = this.copyrightForm.get('copyrightForm').value === 'IEEE';
    this.copyrightForm.get('copyrightForm').valueChanges.subscribe(form => {
      this.enabledIEEECF = form === 'IEEE';
      this.changeDetectorRef.detectChanges();
    });
  }

  listToText(submissionId: number[]): string {
    let text = '';
    submissionId.forEach(id => text += id + '\n');

    return text;
  }

  /** Só os campos do eCF — `submissions` do form não entra na gravação. */
  private ieeeControlValue() {
    const copyrightForm = this.copyrightForm.get('copyrightForm').value;
    return {
      copyrightForm,
      // Derivado, não fonte: mantido no payload porque parte do sistema (a
      // seção de Publicação da submissão, por exemplo) ainda lê o booleano.
      ieeeCopyrightEnable: copyrightForm === 'IEEE',
      pubTitle: this.copyrightForm.get('pubTitle').value,
      artSource: this.copyrightForm.get('artSource').value,
      partNumber: this.copyrightForm.get('partNumber').value
    };
  }

  submitIeeeCopyrightControl() {
    const value = this.ieeeControlValue();

    this.publicationService.editIeeeCopyrightControl(this.event.id, value).subscribe(() => {
      // Espelha no evento em memória — a tela é remontada a partir dele.
      this.event.copyrightForm = value.copyrightForm;
      this.event.ieeeCopyrightEnable = value.ieeeCopyrightEnable;
      this.event.ieeePubTitle = value.pubTitle;
      this.event.ieeeArtSource = value.artSource;
      this.event.ieeePartNumber = value.partNumber;
    }, () => {
      this.notificationService.notifyError('admin.event.publication.controls.load-failed');
    });
  }

  submitFixedControlForm(submissionsId: string[], form: FormFieldCategory) {

    this.navbarService.loadingStart();

    // Os campos IEEE não são mais gravados de carona daqui — eles têm auto-save
    // próprio em `initCopyrightForm`.
    this.publicationService.editSubmissionsAnswers(this.event.id, {
      category: form,
      submissionsId: submissionsId
    }).subscribe(checklistAnswers => {
      this.checklistAnswers[form] = checklistAnswers;
      form == FormFieldCategory.AUTHOR ? this.initAuthorRegistrationAnswersForm() : this.initCopyrightAnswersForm();
    }, () => {}, () => {
      this.navbarService.loadingStop();
    });
  }

  // ─── Tabela unificada de controles ────────────────────────────────────
  // As 3 linhas fixas e as N personalizadas são o MESMO tipo de registro
  // (`CheckListField`). O que difere é só a origem do "Usar?": nas fixas é o
  // flag do evento (que também governa as abas); nas personalizadas é o campo
  // `enabled` do próprio registro.

  /**
   * Papéis EDITÁVEIS nesta tela. O backend honra quatro
   * (`views/submission_checklist.py`); expor revisor e publication chair aqui é
   * decisão de produto ainda em aberto.
   */
  readonly editableRoles: Array<{ name: string, labelKey: string }> = [
    { name: 'author', labelKey: 'forms.fields.author' },
    { name: 'sessionChair', labelKey: 'forms.fields.session-chair' }
  ];

  /**
   * Os três controles FIXOS, na ordem em que aparecem na tabela. `eventFlag` é o
   * booleano no Event que liga/desliga o controle; `category` é o CheckListField
   * correspondente, criado pelo backend quando o flag é ligado — é nele que
   * moram as permissões por papel, que um booleano não teria onde guardar.
   */
  // `titleKey` = frase longa exibida na coluna Título. `descriptionKey` = rótulo
  // CURTO gravado no `description` do registro: a coluna tem `max_length=50` e as
  // frases longas (77 caracteres na do registro de autor) seriam rejeitadas. O
  // curto é o mesmo que a página da submissão exibe.
  readonly fixedControlDefs = [
    { category: FormFieldCategory.AUTHOR,    eventFlag: 'authorRegistrationEnable', titleKey: 'admin.event.publication.controls.form.author-registration', descriptionKey: 'admin.event.publication.controls.registration' },
    { category: FormFieldCategory.COPYRIGHT, eventFlag: 'copyrightEnable',          titleKey: 'admin.event.publication.controls.form.copyright-transfer',  descriptionKey: 'admin.event.publication.controls.copyright' },
    { category: FormFieldCategory.NO_SHOW,   eventFlag: 'noShowEnable',             titleKey: 'admin.event.publication.controls.form.no-show',             descriptionKey: 'admin.event.publication.controls.no-show' }
  ];

  /**
   * Nada nesta tabela vai ao servidor antes do "Salvar" da barra flutuante — as
   * caixas registram INTENÇÃO. Coerente com o resto do sistema (a
   * `reviews-configuration` já funciona assim) e com o próprio controle: caixa
   * de marcação comunica "está marcado neste formulário", enquanto o toggle que
   * havia aqui prometia "liga agora".
   */
  controlsDirty = false;
  controlsSaving = false;

  /**
   * Retrato do último estado SALVO. O "sujo" é uma COMPARAÇÃO com ele, não um
   * sinalizador que liga e nunca desliga: desfazer na mão o que se mexeu — ou
   * desmarcar e remarcar a mesma caixa — devolve a aba ao estado limpo, como o
   * usuário espera.
   */
  private savedSnapshot = '';

  /** Ids de controles removidos na tela, a excluir quando o chair confirmar. */
  private pendingDeletions: number[] = [];

  /** Metadados de cada LINHA, no mesmo índice do `controlsForm`. */
  controlRows: Array<{ fixed: boolean, titleKey?: string, descriptionKey?: string, eventFlag?: string, category?: FormFieldCategory }> = [];

  /**
   * Permissões dos papéis que NÃO se editam aqui (revisor, publication chair),
   * por linha. O backend as honra e a tela de Trilhas as define; sem este resumo
   * ficariam invisíveis. Calculado a cada (re)montagem do form — não é getter de
   * template, senão o `*ngFor` recriaria o DOM a cada ciclo de detecção.
   */
  customControlPermissions: Array<Array<{ roleKey: string, actionKey: string }>> = [];

  private readonly nonEditableRoles: Array<{ name: string, labelKey: string }> = [
    { name: 'reviewer', labelKey: 'forms.fields.reviewer' },
    { name: 'publicationChair', labelKey: 'forms.fields.publication-chair' }
  ];

  permissionControl(row: AbstractControl, group: 'visibleTo' | 'modifiableBy', role: string): FormControl {
    return row.get(group).get(role) as FormControl;
  }

  /**
   * "Editar" exige "Ver": não se edita o que não se enxerga. Quando o Ver de um
   * papel é DESLIGADO, o Editar correspondente cai junto — senão ficaria um
   * `modifiableBy` verdadeiro por trás de um checkbox inerte, e seria gravado
   * assim. O template já bloqueia o Editar visualmente; aqui o dado acompanha.
   */
  onViewChange(row: AbstractControl, role: string): void {
    if (!this.permissionControl(row, 'visibleTo', role).value) {
      this.permissionControl(row, 'modifiableBy', role).setValue(false);
    }
  }

  descriptionControl(row: AbstractControl): FormControl {
    return row.get('description') as FormControl;
  }

  enabledControl(row: AbstractControl): FormControl {
    return row.get('enabled') as FormControl;
  }

  /**
   * Monta a tabela: as três linhas fixas primeiro, depois as personalizadas.
   *
   * As fixas existem SEMPRE, mesmo sem `CheckListField` — ele só é criado quando
   * o controle é ligado pela primeira vez. Nesse caso a linha nasce com
   * `id: null` e permissões desligadas; ao ligar o "Usar?", o backend cria o
   * registro e recarregamos para pegar o id.
   */
  private initControlsForm(): void {
    const byCategory = new Map<string, CheckListField>();
    const customFields: CheckListField[] = [];

    this.checklistFields.forEach(field => {
      if (field.category === FormFieldCategory.CUSTOM) {
        customFields.push(field);
      } else {
        byCategory.set(field.category, field);
      }
    });

    this.controlRows = [];
    const rows: FormGroup[] = [];

    this.fixedControlDefs.forEach(fixed => {
      this.controlRows.push({ fixed: true, titleKey: fixed.titleKey, descriptionKey: fixed.descriptionKey, eventFlag: fixed.eventFlag, category: fixed.category });
      rows.push(this.buildControlRow(byCategory.get(fixed.category), this.event[fixed.eventFlag], fixed.category));
    });

    customFields.forEach(field => {
      this.controlRows.push({ fixed: false });
      rows.push(this.buildControlRow(field, field.enabled !== false, FormFieldCategory.CUSTOM));
    });

    this.controlsForm = this.fb.array(rows);
    this.savedSnapshot = JSON.stringify(this.controlsForm.value);
    this.buildCustomControlPermissions();

    this.previewSubmission = { id: null, event: this.event, noShow: null };
    this.buildPreviewCovers();

    // O preview passa a mostrar o estado PENDENTE — "como vai ficar se eu
    // confirmar" —, que é mais útil que espelhar o que já está salvo.
    this.controlsForm.valueChanges.subscribe(() => {
      this.updateDirty();
      this.buildPreviewCovers();
    });
  }

  // ─── Pré-visualização ─────────────────────────────────────────────────
  // Mostra a seção "Publicação" da página da submissão como ela ficará para o
  // autor e para o coordenador de sessão. Não é uma imitação: renderiza o MESMO
  // componente daquela seção (`app-paper-publication-info`), em modo
  // `previewOnly` — é o que garante que preview e realidade não divirjam.

  /** Submissão de fachada: o componente só precisa do evento (e, fora do
   *  preview, do id — que aqui nunca é usado, porque nada é gravado). */
  previewSubmission: any;

  /**
   * Papéis pré-visualizados — NÃO é a mesma lista das colunas da tabela. O TPC
   * chair não tem colunas lá (ele sempre vê e sempre edita, não há o que
   * configurar), mas é um dos perfis que o chair precisa conferir.
   */
  readonly previewRoles: Array<{ name: string, labelKey: string }> = [
    { name: 'author', labelKey: 'forms.fields.author' },
    { name: 'sessionChair', labelKey: 'forms.fields.session-chair' },
    { name: 'chair', labelKey: 'forms.fields.chairs' }
  ];

  previewCovers: { [role: string]: SubmissionCoverage[] } = { chair: [], author: [], sessionChair: [] };

  /** Respostas experimentadas no preview, por id de controle — sobrevivem à
   *  reconstrução dos covers quando a tabela é reconfigurada. */
  private previewAnswers = new Map<number, boolean>();

  /**
   * Um dos previews mudou (marcação ou apresentação). Os covers são os MESMOS
   * objetos nos dois papéis, então o modelo já está sincronizado — o que falta é
   * avisar o outro `app-paper-publication-info`, que monta o próprio form no
   * `ngOnChanges` e só reage a uma REFERÊNCIA de array nova.
   */
  onPreviewChange(): void {
    this.controlsForm.value.forEach((row, index) => {
      const cover = this.previewCovers.chair
        .find(item => item.field.id === (row.id !== null ? row.id : -(index + 1)));

      if (cover) {
        this.previewAnswers.set(cover.field.id, cover.answer);
      }
    });

    const refreshed: { [role: string]: SubmissionCoverage[] } = {};
    this.previewRoles.forEach(role => refreshed[role.name] = [...this.previewCovers[role.name]]);
    this.previewCovers = refreshed;
  }

  /**
   * Traduz a tabela para o que cada papel enxergaria: só os controles EM USO e
   * marcados como visíveis para aquele papel. `modifiableBy` vai junto — é ele
   * que o `canEdit` do componente usa pra decidir entre marcável e travado.
   *
   * Recalculado a cada mudança do form (arrays NOVOS, pra disparar o
   * `ngOnChanges` do preview), nunca num getter de template.
   */
  private buildPreviewCovers(): void {
    const covers: { [role: string]: SubmissionCoverage[] } = { chair: [], author: [], sessionChair: [] };

    this.controlsForm.value.forEach((row, index) => {
      if (!row.enabled) {
        return;
      }

      const field: any = {
        // Sem id (controle fixo nunca ligado) o componente não teria nome de
        // control; o índice serve de chave estável dentro do preview.
        id: row.id !== null ? row.id : -(index + 1),
        category: row.category,
        description: this.controlRows[index].fixed
          ? this.translate.instant(this.controlRows[index].titleKey)
          : row.description,
        visibleTo: row.visibleTo,
        modifiableBy: row.modifiableBy
      };

      // UM cover por controle, compartilhado pelos dois papéis. É o que
      // sincroniza as visões: marcar no preview do autor muda o MESMO objeto que
      // o preview do coordenador lê — como seria na submissão real, onde os dois
      // olham para o mesmo dado. Respostas já experimentadas são preservadas
      // quando a tabela é reconfigurada.
      const cover = { field, answer: this.previewAnswers.get(field.id) === true };
      this.previewAnswers.set(field.id, cover.answer);

      // O TPC chair vê TODOS os controles em uso — não há permissão a consultar,
      // é o dono da configuração. Autor e coordenador entram por visibilidade.
      covers.chair.push(cover);

      this.editableRoles.forEach(role => {
        if (row.visibleTo[role.name] || row.modifiableBy[role.name]) {
          covers[role.name].push(cover);
        }
      });
    });

    this.previewCovers = covers;
  }

  /**
   * O botão "Enviar ao IEEE CS" aparece no preview sob a MESMA regra da página
   * da submissão (`paper-info.setShowIeeeCopyright`): eCF ligado no evento E o
   * usuário sendo chair ou AUTOR — coordenador de sessão não vê o botão.
   *
   * O status ACCEPTED da submissão, que também entra na regra lá, é premissa
   * aqui: o preview ilustra justamente uma submissão aceita.
   */
  previewShowIeeeCopyright(role: string): boolean {
    return !!this.event.ieeeCopyrightEnable && role !== 'sessionChair';
  }

  /** Uma linha. `field` pode não existir (controle fixo nunca ligado). */
  private buildControlRow(field: CheckListField, enabled: boolean, category: FormFieldCategory): FormGroup {
    return this.fb.group({
      id: [field ? field.id : null],
      category: [category],
      // O título só é obrigatório nas linhas PERSONALIZADAS — nelas ele é o que
      // o chair digita. As fixas têm rótulo próprio (i18n) e nascem no backend
      // SEM description; exigir o campo delas invalidava o form inteiro e, como
      // `commitControls()` desiste quando o form está inválido, NADA era salvo.
      description: [
        field ? field.description : '',
        category === FormFieldCategory.CUSTOM
          ? [Validators.required, Validators.minLength(1), Validators.maxLength(50)]
          : [Validators.maxLength(50)]
      ],
      enabled: [enabled],
      visibleTo: this.fb.group({
        author: [!!(field && field.visibleTo.author)],
        reviewer: [!!(field && field.visibleTo.reviewer)],
        sessionChair: [!!(field && field.visibleTo.sessionChair)],
        publicationChair: [!!(field && field.visibleTo.publicationChair)]
      }),
      modifiableBy: this.fb.group({
        author: [!!(field && field.modifiableBy.author)],
        reviewer: [!!(field && field.modifiableBy.reviewer)],
        sessionChair: [!!(field && field.modifiableBy.sessionChair)],
        publicationChair: [!!(field && field.modifiableBy.publicationChair)]
      })
    });
  }

  private buildCustomControlPermissions(): void {
    this.customControlPermissions = this.controlsForm.controls.map(row => {
      const value = row.value;
      return this.nonEditableRoles
        .filter(role => value.visibleTo[role.name] || value.modifiableBy[role.name])
        .map(role => ({
          roleKey: role.labelKey,
          actionKey: value.modifiableBy[role.name]
            ? 'admin.event.publication.controls.form.custom-control.permissions.view-edit'
            : 'admin.event.publication.controls.form.custom-control.permissions.view'
        }));
    });
  }

  /**
   * Baixa o `dirty` SÓ depois que o layout assentar.
   *
   * É o `dirty` que manda a barra flutuante voltar ao lar, e o pouso é uma
   * animação FLIP: ela mede a posição de destino no instante em que começa.
   * Salvar e descartar remontam a tabela e o preview — ambos ACIMA da barra —,
   * então baixar o `dirty` no mesmo quadro faz a barra mirar um lar que ainda
   * vai se mover: os botões terminam deslocados da caixa.
   *
   * Dois quadros: o primeiro deixa o Angular renderizar, o segundo deixa o
   * navegador refazer o layout. Fora da zona pra não disparar detecção à toa; o
   * `run` devolve o controle ao Angular.
   *
   * Usado pelos DOIS caminhos — foi por aplicar só no salvar que o descartar
   * continuou quebrado.
   */
  private clearDirtyWhenSettled(): void {
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.ngZone.run(() => this.controlsDirty = false);
      }));
    });
  }

  /** Há diferença entre o que está na tela e o último estado salvo? */
  private updateDirty(): void {
    const dirty = this.pendingDeletions.length > 0
      || JSON.stringify(this.controlsForm.value) !== this.savedSnapshot;

    // FICAR sujo é imediato: a barra deve decolar assim que houver alteração.
    if (dirty) {
      this.controlsDirty = true;
      return;
    }

    // VOLTAR ao limpo espera o layout assentar, pelo mesmo motivo do salvar e do
    // descartar: baixar o `dirty` aqui dispara a volta ao lar, e a animação mede
    // a posição de destino no instante em que começa. A mudança que acabou de
    // limpar o formulário ainda vai remontar o preview (e possivelmente as
    // abas), movendo o lar depois da medição — a barra apareceria deslocada no
    // início do percurso.
    this.clearDirtyWhenSettled();
  }

  /**
   * ABAS das linhas fixas.
   *
   * VISÍVEL segue o estado pendente — a aba aparece ou some no mesmo instante em
   * que a caixa muda, dando retorno imediato ao que se está montando.
   *
   * HABILITADA exige também o estado salvo: uma aba recém-criada fica visível
   * porém inerte até o "Salvar", porque antes disso o controle não existe no
   * servidor e a aba não teria dados. Ao desmarcar, ela simplesmente some.
   */
  private pendingFlag(eventFlag: string): boolean {
    const index = this.fixedControlDefs.findIndex(def => def.eventFlag === eventFlag);
    return index >= 0 && this.controlsForm && this.controlsForm.at(index).value.enabled;
  }

  tabVisible(eventFlag: string): boolean {
    // Segue a CAIXA, nos dois sentidos: marcar faz a aba aparecer na hora,
    // desmarcar faz sumir na hora. O "Descartar" recarrega do servidor e as
    // abas voltam ao estado salvo.
    return this.pendingFlag(eventFlag);
  }

  tabEnabled(eventFlag: string): boolean {
    return !!this.event[eventFlag] && this.pendingFlag(eventFlag);
  }

  /**
   * Grava tudo o que a aba acumulou, em ordem:
   *   1. flags do evento — é o que faz o backend CRIAR o CheckListField dos
   *      controles fixos recém-ligados;
   *   2. releitura, pra essas linhas receberem o id;
   *   3. criação/edição dos controles (permissões, títulos, `enabled`);
   *   4. exclusões pendentes;
   *   5. releitura final, que remonta a tabela já no estado salvo.
   *
   * A ordem importa: sem o passo 1 antes do 3, as permissões de um controle
   * fixo recém-ligado não teriam onde ser gravadas.
   */
  saveControls(): void {
    if (this.controlsSaving) {
      return;
    }

    this.controlsSaving = true;

    // Estado dos flags ANTES de salvar. As respostas de cada categoria são
    // baixadas no `initComponent` só quando o flag já está ligado (é a
    // requisição mais cara da tela); ligar o controle aqui faz a aba nascer sem
    // que ninguém tenha buscado o conteúdo dela. Guardar o "antes" é o que
    // permite detectar a transição desligado→ligado e buscar só nesse caso.
    const wasEnabled = {
      authorRegistrationEnable: !!this.event.authorRegistrationEnable,
      copyrightEnable: !!this.event.copyrightEnable
    };

    const flags = {};
    this.fixedControlDefs.forEach((def, index) => {
      flags[def.eventFlag] = this.controlsForm.at(index).value.enabled;
    });

    this.publicationService.editPublicationControls(this.event.id, flags).pipe(
      switchMap(res => {
        this.event.authorRegistrationEnable = res.data.authorRegistrationEnable;
        this.event.copyrightEnable = res.data.copyrightEnable;
        this.event.noShowEnable = res.data.noShowEnable;
        return this.publicationService.getEventCheckListFields(this.eventId, true);
      }),
      switchMap(fields => {
        this.mergeFixedIds(fields);
        return this.saveChecklist();
      }),
      switchMap(() => this.pendingDeletions.length
        ? forkJoin(this.pendingDeletions.map(id => this.publicationService.deleteEventCheckList(id, this.event.id)))
        : of([])),
      switchMap(() => this.publicationService.getEventCheckListFields(this.eventId, true))
    ).subscribe(fields => {
      this.checklistFields = fields;
      this.pendingDeletions = [];

      // NÃO remonta o FormArray. O que está na tela já é o que foi gravado;
      // faltam só os ids dos criados. Remontar recriaria todas as linhas
      // (e o sentinela da barra flutuante junto) bem no meio dos 250ms da
      // animação de volta ao lar — e o `updateAwayState` do sentinela cancela a
      // animação em curso ao ser disparado. Era isso que a interrompia.
      this.mergeCreatedIds(fields);
      this.savedSnapshot = JSON.stringify(this.controlsForm.value);
      this.buildCustomControlPermissions();
      this.buildPreviewCovers();

      this.controlsSaving = false;
      // Abas recém-ligadas: buscar o conteúdo que a carga inicial pulou.
      this.loadAnswersForNewlyEnabled(wasEnabled);
      // Recarrega o evento no cache: os flags governam as abas e o menu.
      this.adminService.fetchSelectedEvent(this.event.id);
      this.clearDirtyWhenSettled();
    }, () => {
      this.controlsSaving = false;
      // Mensagem HONESTA: a cadeia acima é uma SEQUÊNCIA de requisições
      // (flags → campos → checklist → exclusões → campos), e o `switchMap`
      // aborta na primeira falha — os passos anteriores JÁ gravaram. Antes
      // este ramo exibia `load-failed` ("não foi possível CARREGAR"), numa
      // ação de salvar: a mensagem não batia com o que o usuário fez nem com
      // o que aconteceu (issue #2381).
      this.notificationService.notifyError('admin.event.publication.controls.save-failed');

      // Recarrega do servidor. Sem isto a tela ficava mostrando o estado
      // pretendido — não o gravado — e seguia marcada como suja, então o
      // UnsavedChangesGuard barrava a saída por alterações que, em parte, já
      // estavam salvas. Recarregar faz a tela contar a verdade e o `dirty`
      // voltar a ser confiável (`initControlsForm` refaz o snapshot).
      this.reloadAfterFailedSave();
    });
  }

  /** Re-sincroniza a tela com o servidor depois de um save que falhou no meio.
   *  Mesmo caminho da carga inicial; `pendingDeletions` é descartado porque
   *  não se sabe quais exclusões chegaram a ser aplicadas — o servidor passa a
   *  ser a fonte da verdade. */
  private reloadAfterFailedSave(): void {
    this.pendingDeletions = [];
    this.publicationService.getPublicationEventInfo(this.eventId).subscribe(publicationEventInfo => {
      this.event = publicationEventInfo;
      this.initComponent();
      this.controlsDirty = false;
    }, () => {
      // Recarga também falhou (backend fora do ar, por exemplo). Mantém o
      // `dirty` para o guard continuar protegendo o que o usuário digitou.
      this.notificationService.notifyError('admin.event.publication.controls.load-failed');
    });
  }

  /**
   * Busca as respostas das abas que acabaram de ser LIGADAS.
   *
   * O `initComponent` pula essa requisição quando o controle está desligado — a
   * aba nem existe, e é a chamada mais cara da tela. Quando o chair liga o
   * controle e confirma, a aba aparece: sem isto ela apareceria vazia até um
   * recarregamento da página.
   *
   * Só a transição desligado→ligado dispara. Quem já estava ligado teve os
   * dados baixados na carga e não é rebuscado.
   */
  private loadAnswersForNewlyEnabled(wasEnabled: { authorRegistrationEnable: boolean, copyrightEnable: boolean }): void {
    if (!wasEnabled.authorRegistrationEnable && this.event.authorRegistrationEnable) {
      this.publicationService.getSubmissionsAnswers(this.eventId, {category: FormFieldCategory.AUTHOR}).subscribe(answers => {
        this.checklistAnswers.AUTHOR_REGISTRATION = answers;
        this.initAuthorRegistrationForm();
      }, () => {
        this.notificationService.notifyError('admin.event.publication.controls.load-failed');
      });
    }

    if (!wasEnabled.copyrightEnable && this.event.copyrightEnable) {
      this.publicationService.getSubmissionsAnswers(this.eventId, {category: FormFieldCategory.COPYRIGHT}).subscribe(answers => {
        this.checklistAnswers.COPYRIGHT = answers;
        this.initCopyrightForm();
      }, () => {
        this.notificationService.notifyError('admin.event.publication.controls.load-failed');
      });
    }
  }

  /** "Descartar": recarrega do servidor e joga fora tudo o que estava pendente. */
  discardControls(): void {
    this.pendingDeletions = [];

    this.publicationService.getEventCheckListFields(this.eventId, true).subscribe(fields => {
      this.checklistFields = fields;
      this.initControlsForm();
      this.clearDirtyWhenSettled();
    }, () => {
      this.notificationService.notifyError('admin.event.publication.controls.load-failed');
    });
  }

  /**
   * Atribui, nas linhas que ainda não têm id, o do registro correspondente
   * recém-criado — fixas pela categoria, personalizadas pela ordem.
   */
  private mergeCreatedIds(fields: CheckListField[]): void {
    this.mergeFixedIds(fields);

    const customIds = fields
      .filter(field => field.category === FormFieldCategory.CUSTOM)
      .map(field => field.id);

    let position = 0;
    this.controlsForm.controls.forEach(row => {
      if (row.value.category !== FormFieldCategory.CUSTOM) {
        return;
      }

      if (row.value.id === null && customIds[position] !== undefined) {
        row.get('id').setValue(customIds[position], { emitEvent: false });
      }

      position++;
    });
  }

  /** Copia pros controles fixos o id do registro que o backend acabou de criar. */
  private mergeFixedIds(fields: CheckListField[]): void {
    this.fixedControlDefs.forEach((def, index) => {
      const row = this.controlsForm.at(index);

      if (row.value.id !== null) {
        return;
      }

      const field = fields.find(item => item.category === def.category);

      if (field) {
        row.get('id').setValue(field.id, { emitEvent: false });
      }
    });
  }

  /** Nova linha personalizada, em branco e com o foco no título. */
  addNewChecklistField(): void {
    this.controlRows.push({ fixed: false });
    this.controlsForm.push(this.buildControlRow(null, true, FormFieldCategory.CUSTOM));
    this.customControlPermissions.push([]);

    // Renderiza a linha agora, no mesmo clique, pra que o ViewChildren já a
    // enxergue e o foco caia nela sem passar por uma tarefa assíncrona.
    this.changeDetectorRef.detectChanges();

    const inputs = this.customControlInputs.toArray();
    inputs[inputs.length - 1].nativeElement.focus();
  }

  deleteChecklistField(index: number): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.tracks.checklist.confirm-checklistfield-deletion',
        content: ''
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      const checklistId = this.controlsForm.at(index).value.id;

      // Exclusão PENDENTE: some da tela agora, mas o DELETE só sai junto com o
      // "Salvar". Misturar "isto já foi" com "isto está por confirmar" na mesma
      // tabela é o que confundiria.
      if (checklistId) {
        this.pendingDeletions.push(checklistId);
      }

      this.controlsForm.removeAt(index);
      this.controlRows.splice(index, 1);
      this.customControlPermissions.splice(index, 1);
    });
  }

  saveChecklist(): Observable<CheckListField[][]> {
    const fieldsToCreate = [];
    const fieldsToEdit = [];

    // Uma passada pela tabela inteira — fixos e personalizados são o MESMO tipo
    // de registro. Linhas fixas ainda sem CheckListField (controle nunca ligado)
    // não têm o que gravar: são puladas.
    this.controlsForm.value.forEach((field, index) => {
      const isFixed = field.category !== FormFieldCategory.CUSTOM;

      if (isFixed && field.id === null) {
        return;
      }

      field.order = index;
      field.type = FormFieldType.CHECKBOX;

      // O backend cria os controles fixos sem `description`, e o serializer não
      // aceita string vazia (a coluna é NOT NULL sem blank). Preenchemos com o
      // rótulo traduzido — que é o mesmo texto exibido na tabela.
      if (isFixed && !field.description) {
        field.description = this.translate.instant(this.controlRows[index].descriptionKey);
      }

      if (field.id !== null) {
        fieldsToEdit.push(field);
      } else {
        fieldsToCreate.push(field);
      }
    });

    return forkJoin([
      this.publicationService.createEventCheckList(fieldsToCreate, this.event.id),                                // Create new checklist items.
      this.publicationService.editEventCheckList(fieldsToEdit, this.event.id)                                     // Edit existing items.
    ]);
  }

}
